import React from 'react';
import { useState } from 'react';
import { 
  MessageCircle, Users, Heart, Share2, BookOpen, Award, 
  TrendingUp, Clock, MapPin, Star, ThumbsUp, ThumbsDown,
  Search, Filter, Plus, Bell, Settings, ChevronRight,
  Eye, MessageSquare, UserPlus, Calendar, Video,
  Bookmark, Flag, MoreHorizontal, Send, Smile,
  Hash, AtSign, Image, Link, Mic, Camera, X,
  Crown, Zap, Target, Globe, Lock, UserCheck,
  Lightbulb, HelpCircle, CheckCircle, AlertCircle,
  Gift, Coffee, Book, Code, Brain, Cpu
} from 'lucide-react';

const CommunityLearning = ({ darkMode }) => {
  const [activeTab, setActiveTab] = useState('feed');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreatePost, setShowCreatePost] = useState(false);

  // Sample community data
  const communityFeed = [
    {
      id: 1,
      user: {
        name: 'Sarah Chen',
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
        level: 'Expert',
        badge: 'JavaScript Ninja',
        isVerified: true
      },
      type: 'achievement',
      timestamp: '2 hours ago',
      content: {
        title: 'Just completed Advanced React Patterns course! 🎉',
        description: 'The render props and compound components patterns were mind-blowing. Here are my key takeaways...',
        tags: ['react', 'javascript', 'frontend'],
        likes: 24,
        comments: 8,
        shares: 3
      },
      engagement: {
        liked: false,
        bookmarked: true
      }
    },
    {
      id: 2,
      user: {
        name: 'Marcus Rodriguez',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
        level: 'Intermediate',
        badge: 'Data Explorer',
        isVerified: false
      },
      type: 'question',
      timestamp: '4 hours ago',
      content: {
        title: 'Best practices for handling large datasets in Python?',
        description: 'Working with a 10GB CSV file and running into memory issues. What are your go-to strategies for efficient data processing?',
        tags: ['python', 'data-science', 'performance'],
        likes: 12,
        comments: 15,
        shares: 2,
        answers: 3,
        bestAnswer: true
      },
      engagement: {
        liked: true,
        bookmarked: false
      }
    },
    {
      id: 3,
      user: {
        name: 'Emma Wilson',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
        level: 'Beginner',
        badge: 'Rising Star',
        isVerified: false
      },
      type: 'study_group',
      timestamp: '6 hours ago',
      content: {
        title: 'Starting a Machine Learning study group - Join us!',
        description: 'Looking for 4-5 motivated learners to tackle Andrew Ng\'s ML course together. Weekly video calls + shared projects.',
        tags: ['machine-learning', 'study-group', 'collaboration'],
        likes: 31,
        comments: 22,
        shares: 8,
        participants: 12,
        maxParticipants: 15
      },
      engagement: {
        liked: false,
        bookmarked: true
      }
    }
  ];

  const studyGroups = [
    {
      id: 1,
      name: 'JavaScript Mastery Circle',
      description: 'Advanced JS concepts, design patterns, and best practices',
      members: 147,
      activity: 'Very Active',
      category: 'Programming',
      tags: ['javascript', 'advanced', 'patterns'],
      meetingTime: 'Wednesdays 7PM EST',
      isPrivate: false,
      moderator: 'Alex Thompson'
    },
    {
      id: 2,
      name: 'AI/ML Research Group',
      description: 'Discussing latest papers, implementing algorithms, sharing insights',
      members: 89,
      activity: 'Active',
      category: 'AI/ML',
      tags: ['machine-learning', 'research', 'papers'],
      meetingTime: 'Sundays 3PM EST',
      isPrivate: false,
      moderator: 'Dr. Lisa Park'
    },
    {
      id: 3,
      name: 'Full-Stack Builders',
      description: 'Building real projects together, code reviews, and mentorship',
      members: 203,
      activity: 'Very Active',
      category: 'Web Development',
      tags: ['fullstack', 'projects', 'mentorship'],
      meetingTime: 'Daily standups',
      isPrivate: false,
      moderator: 'Ryan Kumar'
    }
  ];

  const discussions = [
    {
      id: 1,
      title: 'Should I learn TypeScript before diving into React?',
      author: 'newbie_coder',
      category: 'Programming',
      replies: 23,
      views: 156,
      lastActivity: '1 hour ago',
      tags: ['typescript', 'react', 'beginner'],
      isHot: true,
      hasAcceptedAnswer: true
    },
    {
      id: 2,
      title: 'Best resources for learning system design?',
      author: 'aspiring_architect',
      category: 'System Design',
      replies: 31,
      views: 287,
      lastActivity: '3 hours ago',
      tags: ['system-design', 'architecture', 'resources'],
      isHot: true,
      hasAcceptedAnswer: false
    },
    {
      id: 3,
      title: 'How to stay motivated during long learning journeys?',
      author: 'persistent_learner',
      category: 'General',
      replies: 45,
      views: 412,
      lastActivity: '5 hours ago',
      tags: ['motivation', 'learning-tips', 'productivity'],
      isHot: false,
      hasAcceptedAnswer: true
    }
  ];

  const topContributors = [
    {
      name: 'Alex Thompson',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      reputation: 2847,
      contributions: 156,
      specialty: 'JavaScript & React',
      badge: 'Community Leader'
    },
    {
      name: 'Dr. Lisa Park',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      reputation: 2234,
      contributions: 98,
      specialty: 'Machine Learning',
      badge: 'ML Expert'
    },
    {
      name: 'Ryan Kumar',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      reputation: 1923,
      contributions: 134,
      specialty: 'Full-Stack Development',
      badge: 'Code Mentor'
    }
  ];

  const learningChallenges = [
    {
      id: 1,
      title: '30-Day Python Challenge',
      description: 'Build a different project every day for 30 days',
      participants: 1247,
      timeLeft: '12 days',
      difficulty: 'Intermediate',
      prize: 'Certificate + Badge',
      isActive: true
    },
    {
      id: 2,
      title: 'React Component Library',
      description: 'Collaborate to build a comprehensive UI component library',
      participants: 89,
      timeLeft: '45 days',
      difficulty: 'Advanced',
      prize: 'Portfolio Project',
      isActive: true
    }
  ];

  const trendingTopics = [
    { name: 'React Hooks', posts: 142 },
    { name: 'Machine Learning', posts: 98 },
    { name: 'System Design', posts: 76 },
    { name: 'TypeScript', posts: 64 },
    { name: 'Data Structures', posts: 52 }
  ];

  const upcomingEvents = [
    {
      title: 'React Workshop',
      date: 'Tomorrow, 2PM',
      attendees: 45
    },
    {
      title: 'ML Paper Discussion',
      date: 'Wed, 7PM',
      attendees: 23
    },
    {
      title: 'Code Review Session',
      date: 'Fri, 6PM',
      attendees: 31
    }
  ];

  const getPostTypeIcon = (type) => {
    switch (type) {
      case 'achievement': return Award;
      case 'question': return HelpCircle;
      case 'study_group': return Users;
      default: return MessageCircle;
    }
  };

  const getPostTypeColor = (type) => {
    switch (type) {
      case 'achievement': return 'text-yellow-500';
      case 'question': return 'text-blue-500';
      case 'study_group': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-50 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-8">
              <h1 className="text-2xl font-bold flex items-center space-x-2">
                <Users className="w-6 h-6 text-blue-500" />
                <span>Community</span>
              </h1>
              
              <nav className="hidden md:flex items-center space-x-6">
                {[
                  { id: 'feed', label: 'Feed', icon: Globe },
                  { id: 'discussions', label: 'Discussions', icon: MessageCircle },
                  { id: 'groups', label: 'Study Groups', icon: Users },
                  { id: 'challenges', label: 'Challenges', icon: Target },
                  { id: 'leaderboard', label: 'Leaderboard', icon: Crown }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                      activeTab === tab.id 
                        ? 'bg-blue-500 text-white' 
                        : darkMode 
                          ? 'hover:bg-gray-700 text-gray-300' 
                          : 'hover:bg-gray-100 text-gray-600'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <input
                  type="search"
                  placeholder="Search community..."
                  className={`pl-10 pr-4 py-2 rounded-xl border ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
              
              <button className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors relative`}>
                <Bell className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              </button>              
             
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {activeTab === 'feed' && (
              <div className="space-y-6">
                {/* Create Post */}
                <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                  <div className="flex items-center space-x-4">
                    <img 
                      src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
                      alt="You"
                      className="w-12 h-12 rounded-full"
                    />
                    <button 
                      onClick={() => setShowCreatePost(true)}
                      className={`flex-1 text-left px-4 py-3 rounded-xl ${
                        darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                      } transition-colors`}
                    >
                      Share your learning journey...
                    </button>
                    <div className="flex items-center space-x-2">
                      <button className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}>
                        <Image className="w-5 h-5" />
                      </button>
                      <button className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}>
                        <Video className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Feed Posts */}
                {communityFeed.map((post) => {
                  const PostIcon = getPostTypeIcon(post.type);
                  return (
                    <div key={post.id} className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg hover:shadow-xl transition-all duration-300`}>
                      <div className="flex items-start space-x-4">
                        <img 
                          src={post.user.avatar}
                          alt={post.user.name}
                          className="w-12 h-12 rounded-full"
                        />
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold">{post.user.name}</h3>
                            {post.user.isVerified && (
                              <CheckCircle className="w-4 h-4 text-blue-500" />
                            )}
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              post.user.level === 'Expert' ? 'bg-purple-100 text-purple-700' :
                              post.user.level === 'Intermediate' ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {post.user.level}
                            </span>
                            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {post.timestamp}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-2 mb-3">
                            <PostIcon className={`w-4 h-4 ${getPostTypeColor(post.type)}`} />
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                              darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {post.user.badge}
                            </span>
                          </div>
                          
                          <h4 className="text-lg font-medium mb-2">{post.content.title}</h4>
                          <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {post.content.description}
                          </p>
                          
                          <div className="flex flex-wrap gap-2 mb-4">
                            {post.content.tags.map((tag, index) => (
                              <span key={index} className={`px-2 py-1 text-xs rounded-full ${
                                darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'
                              }`}>
                                #{tag}
                              </span>
                            ))}
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-6">
                              <button className={`flex items-center space-x-2 ${
                                post.engagement.liked ? 'text-red-500' : darkMode ? 'text-gray-400 hover:text-red-400' : 'text-gray-600 hover:text-red-500'
                              } transition-colors`}>
                                <Heart className={`w-5 h-5 ${post.engagement.liked ? 'fill-current' : ''}`} />
                                <span>{post.content.likes}</span>
                              </button>
                              
                              <button className={`flex items-center space-x-2 ${darkMode ? 'text-gray-400 hover:text-blue-400' : 'text-gray-600 hover:text-blue-500'} transition-colors`}>
                                <MessageCircle className="w-5 h-5" />
                                <span>{post.content.comments}</span>
                              </button>
                              
                              <button className={`flex items-center space-x-2 ${darkMode ? 'text-gray-400 hover:text-green-400' : 'text-gray-600 hover:text-green-500'} transition-colors`}>
                                <Share2 className="w-5 h-5" />
                                <span>{post.content.shares}</span>
                              </button>
                            </div>
                            
                            <button className={`${
                              post.engagement.bookmarked ? 'text-yellow-500' : darkMode ? 'text-gray-400 hover:text-yellow-400' : 'text-gray-600 hover:text-yellow-500'
                            } transition-colors`}>
                              <Bookmark className={`w-5 h-5 ${post.engagement.bookmarked ? 'fill-current' : ''}`} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {activeTab === 'discussions' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-light">Community Discussions</h2>
                  <button className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors">
                    <Plus className="w-4 h-4" />
                    <span>New Discussion</span>
                  </button>
                </div>
                
                <div className="space-y-4">
                  {discussions.map((discussion) => (
                    <div key={discussion.id} className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="text-lg font-medium hover:text-blue-500 transition-colors">
                              {discussion.title}
                            </h3>
                            {discussion.isHot && (
                              <span className="px-2 py-1 text-xs bg-red-500 text-white rounded-full">
                                🔥 Hot
                              </span>
                            )}
                            {discussion.hasAcceptedAnswer && (
                              <CheckCircle className="w-4 h-4 text-green-500" />
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                            <span>by {discussion.author}</span>
                            <span>•</span>
                            <span>{discussion.category}</span>
                            <span>•</span>
                            <span>{discussion.lastActivity}</span>
                          </div>
                          
                          <div className="flex flex-wrap gap-2 mb-3">
                            {discussion.tags.map((tag, index) => (
                              <span key={index} className={`px-2 py-1 text-xs rounded-full ${
                                darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                                #{tag}
                              </span>
                            ))}
                          </div>
                          
                          <div className="flex items-center space-x-6 text-sm">
                            <div className="flex items-center space-x-1">
                              <MessageCircle className="w-4 h-4" />
                              <span>{discussion.replies} replies</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Eye className="w-4 h-4" />
                              <span>{discussion.views} views</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'groups' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-light">Study Groups</h2>
                  <button className="flex items-center space-x-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors">
                    <Plus className="w-4 h-4" />
                    <span>Create Group</span>
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {studyGroups.map((group) => (
                    <div key={group.id} className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg hover:shadow-xl transition-all duration-300`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <div className={`w-3 h-3 rounded-full ${
                            group.activity === 'Very Active' ? 'bg-green-500' : 
                            group.activity === 'Active' ? 'bg-yellow-500' : 'bg-gray-500'
                          }`}></div>
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {group.activity}
                          </span>
                        </div>
                        {group.isPrivate && <Lock className="w-4 h-4 text-gray-400" />}
                      </div>
                      
                      <h3 className="text-xl font-semibold mb-2">{group.name}</h3>
                      <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {group.description}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {group.tags.map((tag, index) => (
                          <span key={index} className={`px-2 py-1 text-xs rounded-full ${
                            darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'
                          }`}>
                            #{tag}
                          </span>
                        ))}
                      </div>
                      
                      <div className="space-y-2 mb-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <Users className="w-4 h-4" />
                          <span>{group.members} members</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4" />
                          <span>{group.meetingTime}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Crown className="w-4 h-4" />
                          <span>Moderated by {group.moderator}</span>
                        </div>
                      </div>
                      
                      <button className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors">
                        Join Group
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'challenges' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-light">Learning Challenges</h2>
                  <button className="flex items-center space-x-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl transition-colors">
                    <Plus className="w-4 h-4" />
                    <span>Create Challenge</span>
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {learningChallenges.map((challenge) => (
                    <div key={challenge.id} className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg hover:shadow-xl transition-all duration-300`}>
                      <div className="flex items-center justify-between mb-4">
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          challenge.difficulty === 'Advanced' ? 'bg-red-100 text-red-700' :
                          challenge.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {challenge.difficulty}
                        </span>
                        <div className="flex items-center space-x-1 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>{challenge.timeLeft} left</span>
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-semibold mb-2">{challenge.title}</h3>
                      <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {challenge.description}
                      </p>
                      
                      <div className="space-y-3 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span>Participants</span>
                          <span className="font-medium">{challenge.participants}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span>Prize</span>
                          <span className="font-medium text-yellow-500">{challenge.prize}</span>
                        </div>
                      </div>
                      
                      <button className="w-full py-2 px-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-medium transition-all duration-300">
                        Join Challenge
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-light">Community Leaderboard</h2>
                                    <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <select 
                        className={`text-sm rounded-lg px-3 py-1 ${
                          darkMode ? 'bg-gray-700 text-white' : 'bg-white border border-gray-200'
                        }`}
                      >
                        <option>This Month</option>
                        <option>All Time</option>
                        <option>Last 30 Days</option>
                      </select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Filter className="w-4 h-4 text-gray-500" />
                      <select 
                        className={`text-sm rounded-lg px-3 py-1 ${
                          darkMode ? 'bg-gray-700 text-white' : 'bg-white border border-gray-200'
                        }`}
                      >
                        <option>All Categories</option>
                        <option>Programming</option>
                        <option>Data Science</option>
                        <option>Design</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className={`rounded-2xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                  <div className={`grid grid-cols-12 items-center px-6 py-4 ${
                    darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                  } font-medium`}>
                    <div className="col-span-1">Rank</div>
                    <div className="col-span-5">Member</div>
                    <div className="col-span-2">Reputation</div>
                    <div className="col-span-2">Contributions</div>
                    <div className="col-span-2">Specialty</div>
                  </div>

                  {topContributors.map((contributor, index) => (
                    <div 
                      key={contributor.name} 
                      className={`grid grid-cols-12 items-center px-6 py-4 border-b ${
                        darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'
                      } transition-colors`}
                    >
                      <div className="col-span-1 font-bold text-lg">
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                      </div>
                      <div className="col-span-5 flex items-center space-x-4">
                        <img 
                          src={contributor.avatar} 
                          alt={contributor.name} 
                          className="w-10 h-10 rounded-full"
                        />
                        <div>
                          <div className="font-medium">{contributor.name}</div>
                          <div className={`text-xs ${
                            darkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {contributor.badge}
                          </div>
                        </div>
                      </div>
                      <div className="col-span-2 flex items-center space-x-1">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span>{contributor.reputation}</span>
                      </div>
                      <div className="col-span-2">{contributor.contributions}</div>
                      <div className="col-span-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          darkMode ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {contributor.specialty}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                  <h3 className="text-lg font-medium mb-4">How Points Are Calculated</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className={`p-4 rounded-xl ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <MessageCircle className="w-5 h-5 text-blue-500" />
                        <span className="font-medium">Quality Answers</span>
                      </div>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        Upvoted answers earn 10 points per vote. Accepted answers get 25 bonus points.
                      </p>
                    </div>
                    <div className={`p-4 rounded-xl ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <BookOpen className="w-5 h-5 text-green-500" />
                        <span className="font-medium">Tutorials & Guides</span>
                      </div>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        Publishing educational content earns 50 points, plus 5 points per upvote.
                      </p>
                    </div>
                    <div className={`p-4 rounded-xl ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-100'
                    }`}>
                      <div className="flex items-center space-x-2 mb-2">
                        <Users className="w-5 h-5 text-purple-500" />
                        <span className="font-medium">Community Help</span>
                      </div>
                      <p className={`text-sm ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        Organizing study groups or mentoring earns 20 points per participant.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* User Profile Card */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <div className="flex items-center space-x-4 mb-4">
                <img 
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
                  alt="Your profile"
                  className="w-16 h-16 rounded-full"
                />
                <div>
                  <h3 className="font-semibold">Alex Johnson</h3>
                  <div className="flex items-center space-x-1 text-sm text-gray-500">
                    <span>Level 4 Learner</span>
                    <span>•</span>
                    <span>1,245 XP</span>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span>Learning Streak</span>
                  <span className="font-medium">12 days</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ width: '75%' }}
                  ></div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Next Level</span>
                  <span>755/2,000 XP</span>
                </div>
              </div>
            </div>

            {/* Trending Topics */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className="font-medium mb-4 flex items-center space-x-2">
                <TrendingUp className="w-5 h-5" />
                <span>Trending Topics</span>
              </h3>
              <div className="space-y-3">
                {trendingTopics.map((topic, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="hover:text-blue-500 cursor-pointer transition-colors">
                      #{topic.name}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {topic.posts} posts
                    </span>
                  </div>
                ))}
                <button className={`w-full mt-2 text-sm text-center ${
                  darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-700'
                } transition-colors`}>
                  See all topics →
                </button>
              </div>
            </div>

            {/* Upcoming Events */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className="font-medium mb-4 flex items-center space-x-2">
                <Calendar className="w-5 h-5" />
                <span>Upcoming Events</span>
              </h3>
              <div className="space-y-4">
                {upcomingEvents.map((event, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${
                      darkMode ? 'bg-blue-500/20' : 'bg-blue-100'
                    }`}>
                      <Calendar className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-medium">{event.title}</h4>
                      <div className={`text-sm ${
                        darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {event.date} • {event.attendees} attending
                      </div>
                    </div>
                  </div>
                ))}
                <button className={`w-full mt-2 text-sm text-center ${
                  darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-700'
                } transition-colors`}>
                  View calendar →
                </button>
              </div>
            </div>

            {/* Learning Resources */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
              <h3 className="font-medium mb-4 flex items-center space-x-2">
                <BookOpen className="w-5 h-5" />
                <span>Recommended Resources</span>
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${
                    darkMode ? 'bg-purple-500/20' : 'bg-purple-100'
                  }`}>
                    <Book className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <h4 className="font-medium">React Documentation</h4>
                    <div className={`text-sm ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Official docs with new hooks guide
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${
                    darkMode ? 'bg-green-500/20' : 'bg-green-100'
                  }`}>
                    <Video className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <h4 className="font-medium">Python for Beginners</h4>
                    <div className={`text-sm ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Free 4-hour course on YouTube
                    </div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${
                    darkMode ? 'bg-yellow-500/20' : 'bg-yellow-100'
                  }`}>
                    <Code className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <h4 className="font-medium">Algorithm Visualizer</h4>
                    <div className={`text-sm ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Interactive sorting algorithms
                    </div>
                  </div>
                </div>
                <button className={`w-full mt-2 text-sm text-center ${
                  darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-700'
                } transition-colors`}>
                  Browse all resources →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Post Modal */}
      {showCreatePost && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
          darkMode ? 'bg-black/80' : 'bg-black/50'
        }`}>
          <div className={`w-full max-w-2xl rounded-2xl p-6 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          } shadow-xl`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-medium">Create Post</h3>
              <button 
                onClick={() => setShowCreatePost(false)}
                className={`p-2 rounded-xl ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                } transition-colors`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center space-x-4 mb-6">
              <img 
                src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face"
                alt="You"
                className="w-12 h-12 rounded-full"
              />
              <div>
                <div className="font-medium">Alex Johnson</div>
                <select 
                  className={`text-sm rounded-lg px-3 py-1 mt-1 ${
                    darkMode ? 'bg-gray-700 text-white' : 'bg-white border border-gray-200'
                  }`}
                >
                  <option>Public</option>
                  <option>Study Group</option>
                  <option>Followers</option>
                </select>
              </div>
            </div>
            
            <textarea
              placeholder="What are you learning today? Share your thoughts, questions, or achievements..."
              className={`w-full h-40 p-4 rounded-xl border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            ></textarea>
            
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center space-x-2">
                <button className={`p-2 rounded-xl ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                } transition-colors`}>
                  <Image className="w-5 h-5" />
                </button>
                <button className={`p-2 rounded-xl ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                } transition-colors`}>
                  <Video className="w-5 h-5" />
                </button>
                <button className={`p-2 rounded-xl ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                } transition-colors`}>
                  <Link className="w-5 h-5" />
                </button>
                <button className={`p-2 rounded-xl ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                } transition-colors`}>
                  <Code className="w-5 h-5" />
                </button>
              </div>
              
              <button className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors">
                Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityLearning;